/**
 * Number-formatting helpers for Review & Discussion V1 refinement (Feb 2026).
 *
 * Reformats DISPLAY only. Underlying raw values are never mutated.
 * Indian digit grouping · sensible decimals · date/text/id passthrough.
 */

function _indianGroup(intStr) {
  const neg = intStr.startsWith('-');
  const digits = neg ? intStr.slice(1) : intStr;
  if (digits.length <= 3) return (neg ? '-' : '') + digits;
  const lastThree = digits.slice(-3);
  const rest = digits.slice(0, -3);
  return (neg ? '-' : '') + rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree;
}

/** Return `true` if the openpyxl number_format string is percent-like. */
export const isPercentFmt = (nf) => typeof nf === 'string' && nf.includes('%');

/** Return `true` if the format string is date-like. */
export const isDateFmt = (nf) => {
  if (!nf || typeof nf !== 'string') return false;
  const s = nf.toLowerCase();
  return /[dmy]/.test(s) && !s.includes('#') && !s.includes('0.');
};

/** Return `true` if the format string is currency-like. */
export const isCurrencyFmt = (nf) => {
  if (!nf || typeof nf !== 'string') return false;
  return /[₹$€£¥]|"?rs"?|"?inr"?/i.test(nf);
};

/**
 * Format a cell for display given (raw, type, number_format).
 * @param {*} raw - underlying value from the parser
 * @param {string} type - 'n' | 's' | 'd' | 'b' | 'f' | 'empty'
 * @param {string} numberFormat - openpyxl number_format string
 * @param {string} fallbackDisplay - the parser's precomputed display (used for non-numeric)
 * @returns {string} display string
 */
export function formatCellDisplay(raw, type, numberFormat, fallbackDisplay) {
  // Text / booleans / dates — leave untouched
  if (raw == null || raw === '') return '';
  if (type === 's' || type === 'b') return fallbackDisplay ?? String(raw);
  if (type === 'd') return fallbackDisplay ?? String(raw);
  // Formulas already carry a cached raw value we handle below
  const num = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(num)) return fallbackDisplay ?? String(raw);

  // Dates masquerading as numbers — treat as number only if format is date-ish.
  if (isDateFmt(numberFormat)) return fallbackDisplay ?? String(raw);

  // Percent
  if (isPercentFmt(numberFormat)) {
    const pct = num * 100;
    // If already integer OR one-decimal-clean, show without .00
    if (Math.abs(pct - Math.round(pct)) < 1e-9) return `${Math.round(pct)}%`;
    // Preserve up to 2 decimals; drop trailing zeros
    return `${(Math.round(pct * 100) / 100).toString()}%`;
  }

  // Currency + large financial numbers — Indian grouping, ZERO decimals for
  // ordinary revenue/expense/count values (per Feb 2026 spec §4). Fractions
  // are only preserved when the number is small (< 100) or the source format
  // explicitly wants decimals (e.g. "#,##0.00" or currency-with-decimals).
  const hasFraction = Math.abs(num - Math.trunc(num)) > 1e-9;
  const nf = (numberFormat || '').toLowerCase();
  const isBigLikeMoney = Math.abs(num) >= 1000 || isCurrencyFmt(numberFormat) ||
                         (numberFormat && numberFormat.includes('#,##0'));
  if (isBigLikeMoney) {
    // Force whole-rupee display for ordinary large financial numbers.
    // Preserve decimals only if the source format is EXPLICITLY decimal
    // (e.g. "#,##0.00" or "0.00_)") AND the value has a fractional part.
    const formatWantsDecimals = /0\.0+/.test(nf) || /#,##0\.0+/.test(nf);
    if (!formatWantsDecimals || !hasFraction) {
      const grouped = _indianGroup(Math.round(num).toString());
      return isCurrencyFmt(numberFormat) ? `₹${grouped}` : grouped;
    }
    // Source format asks for decimals → show up to 2 dp, drop trailing zeros
    const rounded = Math.round(num * 100) / 100;
    const [intPart, decPart] = rounded.toFixed(2).split('.');
    const grouped = _indianGroup(intPart);
    const decClean = decPart.replace(/0+$/, '');
    const out = decClean ? `${grouped}.${decClean}` : grouped;
    return isCurrencyFmt(numberFormat) ? `₹${out}` : out;
  }

  // Small numbers — show up to 2 dp without trailing zeros
  if (!hasFraction) return String(num);
  const smallRounded = Math.round(num * 100) / 100;
  return String(smallRounded);
}
